/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import {
  PlayerVideoTrack,
  usePlayerController,
} from "../../../../../BasePlayer";
import { useToggleVisibility } from "../../../../Base";

export const useQualitySettings = () => {
  const { player } = usePlayerController();
  const { toggleVisibility } = useToggleVisibility();

  const { t } = usePlayerTranslation();

  const AUTO_TRACK: PlayerVideoTrack = {
    id: "auto",
    label: t("PLAYER_AUTO_QUALITY_TRACK"),
    active: false,
  };

  const [tracks, setTracks] = useState<PlayerVideoTrack[]>(() => {
    return [AUTO_TRACK, ...player.videoTracks()];
  });

  const activeTrack = useMemo(() => {
    const active = tracks.find((track) => track.active);

    return active || tracks.find((track) => track.id === "auto");
  }, [tracks]);

  useEffect(() => {
    const unsubscribe = player.onVideoTrackChange(() => {
      setTracks([AUTO_TRACK, ...player.videoTracks()]);
    });

    return () => unsubscribe();
  }, [player]);

  const onTrackSelect = useCallback(
    (track: PlayerVideoTrack) => {
      player.setVideoTrack(track.id);
      toggleVisibility();
    },
    [player, toggleVisibility],
  );

  return {
    tracks,
    activeTrack,
    onTrackSelect,
  };
};
